import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Award, Building2, Crown } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const TIERS = [
  {
    id: "founders",
    badge: "Founders 100",
    icon: Crown,
    title: "The Founders Club",
    sub: "Limited to one hundred members. Permanent recognition on the Founders Wall.",
    initiation: 25000,
    annual: 6500,
    monthly: 600,
    spots_remaining: 62,
    benefits: [
      "Permanent name on the Founders Wall",
      "Lifetime founding recognition",
      "Founders locker with custom plaque",
      "Priority reservations across the club",
      "Members lounge & VIP lounge access",
      "Conference room & podcast studio credits",
      "Annual founding member gift",
      "Two guest privileges per visit",
      "Priority cigar releases & preferred pricing",
      "Exclusive humidor allocation",
      "First seat at private events",
    ],
    featured: true,
    img: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    id: "executive",
    badge: "Executive",
    icon: Award,
    title: "Executive Membership",
    sub: "Built for professionals, founders, and operators who require a refined home base.",
    initiation: 5000,
    annual: 3800,
    monthly: 360,
    spots_remaining: null,
    benefits: [
      "Members lounge access",
      "Reserved seating priority",
      "Private locker (optional add-on)",
      "Conference room booking",
      "Podcast studio booking",
      "Members-only networking events",
      "Restaurant and bar discounts",
      "Cigar pairing experiences",
      "Guest privileges",
    ],
    img: "https://images.unsplash.com/photo-1617364852223-75f57e78dc96?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    id: "corporate",
    badge: "Corporate",
    icon: Building2,
    title: "Corporate Membership",
    sub: "Memberships for executive teams. Tailored, branded, and built for entertaining.",
    initiation: 15000,
    annual: 12000,
    monthly: null,
    spots_remaining: null,
    benefits: [
      "Up to six executive seats",
      "Conference room credits monthly",
      "Private event hosting privileges",
      "Reserved branded lockers",
      "Business concierge",
      "Preferred reservations",
      "Private whiskey & cigar tastings",
      "Corporate branding opportunities",
      "Client entertaining package",
      "Corporate dashboard (coming soon)",
    ],
    img: "https://images.unsplash.com/photo-1679364297777-1db77b6199be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
];

function PricingRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-3 border-b border-[var(--twr-line)]">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{label}</span>
      <span className="font-serif text-xl text-[var(--twr-bronze)]">{value}</span>
    </div>
  );
}

function Tier({ t }) {
  const Icon = t.icon;
  return (
    <article data-testid={`tier-${t.id}`} className={`surface relative overflow-hidden ${t.featured ? "lg:-mt-6" : ""}`}>
      {t.featured && (
        <div className="absolute top-5 right-5 z-10">
          <span className="tier-badge bg-[#0A0A0A]/80">Most Selective</span>
        </div>
      )}
      <div className="aspect-[16/10] overflow-hidden">
        <img src={t.img} alt={t.title} className="w-full h-full object-cover" />
      </div>
      <div className="p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <Icon size={18} strokeWidth={1.4} className="text-[var(--twr-bronze)]" />
          <p className="eyebrow">{t.badge}</p>
        </div>
        <h3 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">{t.title}</h3>
        <p className="text-[var(--twr-warm-white)]/70 text-sm mt-4 leading-relaxed">{t.sub}</p>

        {t.spots_remaining !== null && (
          <div className="mt-6 flex items-center justify-between text-[12px] tracking-[0.22em] uppercase">
            <span className="text-[var(--twr-warm-white)]/55">Seats Remaining</span>
            <span className="text-[var(--twr-bronze)] font-serif text-2xl">{t.spots_remaining} / 100</span>
          </div>
        )}

        <div className="mt-8">
          {t.initiation > 0 && <PricingRow label="Initiation" value={`$${t.initiation.toLocaleString()}`} />}
          {t.annual && <PricingRow label="Annual" value={`$${t.annual.toLocaleString()}`} />}
          {t.monthly && <PricingRow label="Monthly Option" value={`$${t.monthly.toLocaleString()} / mo`} />}
        </div>

        <ul className="mt-8 space-y-3">
          {t.benefits.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-[var(--twr-warm-white)]/85">
              <Check size={14} strokeWidth={1.8} className="mt-1 text-[var(--twr-bronze)] shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/membership/apply?tier=${t.id}`}
          data-testid={`tier-apply-${t.id}`}
          className={`mt-10 inline-flex w-full ${t.featured ? "btn-bronze" : "btn-ghost"}`}
        >
          Apply for {t.badge} <ChevronRight size={14} />
        </Link>
      </div>
    </article>
  );
}

export default function Membership() {
  const media = useSiteMedia();
  return (
    <div data-testid="membership-page">
      <section className="relative pt-40 pb-24 px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "membership_hero")} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-[1300px] mx-auto text-center">
          <p className="eyebrow">Membership</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            Three tiers. One <span className="italic" style={{ color: "var(--twr-bronze)" }}>standard.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/75 text-base sm:text-lg mt-6 max-w-2xl mx-auto">
            Applications are reviewed by the Founding Committee. Acceptance is by invitation, referral, or written application.
          </p>
        </div>
      </section>

      <section className="section pt-0">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {TIERS.map((t) => <Tier key={t.id} t={{ ...t, img: mediaUrl(media, `membership_tier_${t.id}`) }} />)}
        </div>
      </section>

      <section className="section bg-[#0c0b0a]" data-testid="membership-process">
        <div className="max-w-[1300px] mx-auto">
          <SectionHeader eyebrow="The Process" title="A quiet review. A considered welcome." align="center" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {[
              { n: "01", t: "Application", c: "Submit your written application and a short note on why you'd like to join." },
              { n: "02", t: "Review", c: "Applications are reviewed in confidential waves by the Founding Committee." },
              { n: "03", t: "Conversation", c: "A short in-person conversation with a member of the membership team." },
              { n: "04", t: "Welcome", c: "A signed letter, your locker, and an invitation to the next members' evening." },
            ].map((s) => (
              <div key={s.n} className="surface p-7">
                <p className="font-serif text-3xl text-[var(--twr-bronze)]">{s.n}</p>
                <h4 className="font-serif text-xl mt-4">{s.t}</h4>
                <p className="text-[var(--twr-warm-white)]/65 text-sm mt-3 leading-relaxed">{s.c}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/membership/apply" data-testid="process-cta-apply" className="btn-bronze">Begin Your Application <ChevronRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
